import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api.js";
import { Skeleton, ComingSoonModal } from "@templestay/ui";
import type { FunnelStats } from "@templestay/db";
import type { Test, Survey } from "@templestay/db";
import { useTenantPublicBase } from "@/lib/hooks.js";
import { useAuthStore } from "@/store/auth.js";

type EntityType = "test" | "survey";

type Demographics = {
  language: Record<string, number>;
  resultTypes: Array<{ slug: string; count: number; i18n: string }>;
};

type DevicesData = {
  devices: Array<{ device: string; count: number }>;
  countries: Array<{ country_code: string; count: number }>;
};

const DEVICE_LABELS: Record<string, string> = { mobile: "📱 모바일", tablet: "📟 태블릿", desktop: "🖥 데스크탑" };

export function StatsPage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const PUBLIC_BASE = useTenantPublicBase();
  const { activeTenantId } = useAuthStore();
  const [entityType, setEntityType] = useState<EntityType>("test");
  const [selectedId, setSelectedId] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [qrDownloading, setQrDownloading] = useState(false);

  const { data: tests } = useQuery({
    queryKey: ["tests", activeTenantId],
    queryFn: () => api.get<Test[]>("/api/tests"),
    enabled: !!activeTenantId,
  });

  const { data: surveys } = useQuery({
    queryKey: ["surveys", activeTenantId],
    queryFn: () => api.get<Survey[]>("/api/surveys"),
    enabled: !!activeTenantId,
  });

  const entityList = entityType === "test" ? tests : surveys;

  const queryParam = `survey_id=${selectedId}&type=${entityType}`;

  const { data: funnel, isLoading: funnelLoading } = useQuery({
    queryKey: ["stats-funnel", activeTenantId, entityType, selectedId],
    queryFn: () => api.get<FunnelStats>(`/api/stats/funnel?${queryParam}`),
    enabled: !!activeTenantId && !!selectedId,
  });

  const { data: demographics } = useQuery({
    queryKey: ["stats-demographics", activeTenantId, entityType, selectedId],
    queryFn: () => api.get<Demographics>(`/api/stats/demographics?${queryParam}`),
    enabled: !!activeTenantId && !!selectedId,
  });

  const { data: devicesData } = useQuery({
    queryKey: ["stats-devices", activeTenantId, entityType, selectedId],
    queryFn: () => api.get<DevicesData>(`/api/stats/devices?${queryParam}`),
    enabled: !!activeTenantId && !!selectedId,
  });

  async function handleExport() {
    if (!selectedId) return;
    const data = await api.get<{ rows: Record<string, unknown>[] }>(
      `/api/stats/responses/export?${queryParam}`
    );
    const { utils, writeFile } = await import("xlsx");
    const ws = utils.json_to_sheet(data.rows);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "응답");
    writeFile(wb, `${tenantSlug}-${entityType}-${selectedId}-${Date.now()}.xlsx`);
  }

  async function handleQrDownload() {
    setQrDownloading(true);
    try {
      const res = await fetch(
        `/api/qr/generate?url=${encodeURIComponent(PUBLIC_BASE)}&size=print&format=png`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("QR 생성 실패");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${tenantSlug}-qr-print.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally {
      setQrDownloading(false);
    }
  }

  function handleTypeChange(t: EntityType) {
    setEntityType(t);
    setSelectedId("");
  }

  return (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">통계</h1>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            disabled={!selectedId}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            엑셀 다운로드
          </button>
          <button
            onClick={() => setShowAdvanced(true)}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            고급 통계
          </button>
        </div>
      </div>

      {/* 유형 탭 + 항목 선택 */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-500">유형</label>
          <div className="flex rounded-lg border border-gray-200 bg-white p-1">
            {(["test", "survey"] as const).map((t) => (
              <button
                key={t}
                onClick={() => handleTypeChange(t)}
                className={`rounded-md px-4 py-1.5 text-xs font-semibold transition-colors ${
                  entityType === t ? "bg-brand-500 text-white" : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                {t === "test" ? "🎯 검사" : "📋 설문"}
              </button>
            ))}
          </div>
        </div>
        <div className="min-w-0 flex-1 max-w-sm">
          <label className="mb-1.5 block text-xs font-medium text-gray-500">
            {entityType === "test" ? "검사 선택" : "설문 선택"}
          </label>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400 bg-white"
          >
            <option value="">— 선택하세요 —</option>
            {entityList?.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name} {e.is_active ? "✓" : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Funnel chart */}
      {funnelLoading ? (
        <Skeleton className="h-32 rounded-xl" />
      ) : funnel ? (
        <div className="mb-8 rounded-xl bg-white p-6 shadow-sm ring-1 ring-black/5">
          <h2 className="mb-4 text-sm font-semibold text-gray-700">펀넬</h2>
          <div className="flex items-end gap-0">
            {[
              { label: "메인 진입", value: funnel.main_views },
              { label: entityType === "test" ? "검사 시작" : "설문 시작", value: funnel.survey_starts },
              { label: "동의 완료", value: funnel.consents },
              { label: entityType === "test" ? "검사 완주" : "설문 완주", value: funnel.completions },
            ].map((item, i) => {
              const max = funnel.main_views || 1;
              const pct = Math.round((item.value / max) * 100);
              return (
                <div key={i} className="flex flex-1 flex-col items-center gap-1">
                  <p className="text-lg font-bold text-gray-900">{item.value.toLocaleString()}</p>
                  <div
                    className="w-full rounded-t bg-brand-400 transition-all"
                    style={{ height: `${Math.max(pct, 4)}px`, maxHeight: "80px" }}
                  />
                  <p className="text-xs text-gray-500">{item.label}</p>
                  <p className="text-xs font-semibold text-brand-600">{pct}%</p>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Language breakdown */}
      {demographics?.language && (
        <div className="mb-6 rounded-xl bg-white p-6 shadow-sm ring-1 ring-black/5">
          <h2 className="mb-4 text-sm font-semibold text-gray-700">언어별 응답</h2>
          <div className="space-y-2">
            {Object.entries(demographics.language).map(([lang, count]) => {
              const total = Object.values(demographics.language).reduce((a, b) => a + b, 0);
              const pct = Math.round((count / (total || 1)) * 100);
              return (
                <div key={lang} className="flex items-center gap-3">
                  <span className="w-8 text-sm font-medium text-gray-700">{lang}</span>
                  <div className="flex-1 overflow-hidden rounded-full bg-gray-100">
                    <div className="h-2 rounded-full bg-brand-400 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-10 text-right text-sm text-gray-500">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Result type breakdown (tests only) */}
      {entityType === "test" && demographics?.resultTypes && demographics.resultTypes.length > 0 && (
        <div className="mb-6 rounded-xl bg-white p-6 shadow-sm ring-1 ring-black/5">
          <h2 className="mb-4 text-sm font-semibold text-gray-700">결과 유형별 분포</h2>
          <div className="space-y-2">
            {demographics.resultTypes.map((rt) => {
              const total = demographics.resultTypes.reduce((a, b) => a + b.count, 0);
              const pct = Math.round((rt.count / (total || 1)) * 100);
              const rtLabel = (() => {
                try { return (JSON.parse(rt.i18n) as Record<string, Record<string, string>>).ko?.title ?? rt.slug; }
                catch { return rt.slug; }
              })();
              return (
                <div key={rt.slug} className="flex items-center gap-3">
                  <span className="w-20 truncate text-xs font-medium text-gray-700">{rtLabel}</span>
                  <div className="flex-1 overflow-hidden rounded-full bg-gray-100">
                    <div className="h-2 rounded-full bg-brand-300 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-8 text-right text-xs text-gray-500">{rt.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Device & country breakdown */}
      {devicesData && (
        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {devicesData.devices.length > 0 && (
            <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-black/5">
              <h2 className="mb-4 text-sm font-semibold text-gray-700">디바이스 유형</h2>
              <div className="space-y-2">
                {devicesData.devices
                  .sort((a, b) => b.count - a.count)
                  .map((d) => {
                    const total = devicesData.devices.reduce((s, x) => s + x.count, 0);
                    const pct = Math.round((d.count / (total || 1)) * 100);
                    return (
                      <div key={d.device} className="flex items-center gap-3">
                        <span className="w-28 text-sm text-gray-700">{DEVICE_LABELS[d.device] ?? d.device}</span>
                        <div className="flex-1 overflow-hidden rounded-full bg-gray-100">
                          <div className="h-2 rounded-full bg-indigo-400 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-10 text-right text-sm text-gray-500">{d.count}</span>
                        <span className="w-8 text-right text-xs text-gray-400">{pct}%</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
          {devicesData.countries.length > 0 && (
            <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-black/5">
              <h2 className="mb-4 text-sm font-semibold text-gray-700">국가별 응답 (Top 10)</h2>
              <div className="space-y-2">
                {devicesData.countries.map((c) => {
                  const max = devicesData.countries[0]?.count || 1;
                  const pct = Math.round((c.count / max) * 100);
                  return (
                    <div key={c.country_code} className="flex items-center gap-3">
                      <span className="w-10 text-xs font-mono font-semibold text-gray-600">{c.country_code}</span>
                      <div className="flex-1 overflow-hidden rounded-full bg-gray-100">
                        <div className="h-2 rounded-full bg-teal-400 transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-8 text-right text-sm text-gray-500">{c.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* QR download */}
      <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-black/5">
        <h2 className="mb-1 text-sm font-semibold text-gray-700">인쇄용 QR 코드</h2>
        <p className="mb-4 text-xs text-gray-400">
          사이트로 이동하는 QR — 1200px (약 300dpi, 4×4인치 인쇄 가능)
        </p>
        <div className="flex gap-2">
          <button
            onClick={handleQrDownload}
            disabled={qrDownloading}
            className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {qrDownloading ? "생성 중..." : "PNG 300dpi 다운로드"}
          </button>
          <button
            onClick={async () => {
              const res = await fetch(
                `/api/qr/generate?url=${encodeURIComponent(PUBLIC_BASE)}&size=print&format=svg`,
                { credentials: "include" }
              );
              const blob = await res.blob();
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = `${tenantSlug}-qr-print.svg`;
              a.click();
              URL.revokeObjectURL(a.href);
            }}
            className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            SVG 다운로드
          </button>
        </div>
      </div>

      <ComingSoonModal
        open={showAdvanced}
        onClose={() => setShowAdvanced(false)}
        lang="ko"
        description="문항별 선택률, 기간 비교 등 고급 통계는 본 개발에서 제공됩니다."
      />
    </div>
  );
}
